import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [linkInvalid, setLinkInvalid] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('A palavra-passe deve ter pelo menos 6 caracteres.'); return; }
    if (password !== confirm) { setError('As palavras-passe não coincidem.'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/reset-password`, { token, password });
      setSuccess(data.message || 'Palavra-passe alterada com sucesso.');
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (/expir|inv[aá]lido|utilizado/i.test(detail)) {
        setLinkInvalid(detail || 'Este link já não é válido.');
      } else {
        setError(detail || 'Erro ao redefinir a palavra-passe.');
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <h1 className="font-heading font-semibold text-4xl text-brand-green tracking-tight">nutrianapereira</h1>
          <p className="mt-2 text-brand-muted text-sm">Alimentação saudável para toda a família</p>
        </div>

        <div className="bg-white border border-brand-border rounded-3xl p-6 shadow-[0_8px_32px_rgba(101,113,97,0.04)]">
          <h2 className="font-heading font-medium text-xl text-brand-text mb-6" data-testid="reset-title">
            Nova palavra-passe
          </h2>

          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-2xl text-sm text-red-600" data-testid="reset-error">{error}</div>}
          {linkInvalid ? (
            <div data-testid="reset-link-invalid">
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-2xl text-sm text-amber-800 leading-relaxed">
                <p className="font-medium mb-1">Este link já não é válido</p>
                <p>{linkInvalid} Por favor pede um novo link de acesso à tua nutricionista, ela pode reenviá-lo a qualquer momento a partir do teu perfil.</p>
              </div>
              <Link to="/login" className="block w-full text-center bg-brand-green text-white rounded-full px-6 py-3.5 font-medium hover:bg-brand-green-hover transition-all" data-testid="link-invalid-login-btn">
                Ir para o login
              </Link>
            </div>
          ) : success ? (
            <div data-testid="reset-success">
              <div className="mb-4 p-3 bg-brand-mint/20 border border-brand-mint rounded-2xl text-sm text-brand-green">{success}</div>
              <Link to="/login" className="block w-full text-center bg-brand-green text-white rounded-full px-6 py-3.5 font-medium hover:bg-brand-green-hover transition-all" data-testid="back-to-login-btn">
                Ir para o login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="password" className="text-sm text-brand-muted mb-1.5 block">Nova palavra-passe</Label>
                <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Mínimo 6 caracteres" className="rounded-2xl border-brand-border focus:border-brand-green focus:ring-1 focus:ring-brand-green" required minLength={6} data-testid="reset-password-input" />
              </div>
              <div>
                <Label htmlFor="confirm" className="text-sm text-brand-muted mb-1.5 block">Confirmar palavra-passe</Label>
                <Input id="confirm" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repetir palavra-passe" className="rounded-2xl border-brand-border focus:border-brand-green focus:ring-1 focus:ring-brand-green" required minLength={6} data-testid="reset-confirm-input" />
              </div>
              <button type="submit" disabled={loading} className="w-full bg-brand-green text-white hover:bg-brand-green-hover transition-all duration-300 rounded-full px-6 py-3.5 font-medium disabled:opacity-50" data-testid="reset-submit-btn">
                {loading ? 'A processar...' : 'Redefinir palavra-passe'}
              </button>
            </form>
          )}

          <p className="text-center text-sm text-brand-muted mt-6">
            <Link to="/login" className="text-brand-green font-medium hover:underline" data-testid="reset-back-link">Voltar ao login</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
